import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  OwcIconButton,
  OwcTypography,
  OwcProgressSpinner,
  OwcChip,
  OwcCheckbox,
  OwcInput,
} from "@one/react";

const MANUAL_PROJECT_MAX_LENGTH = 30;
const MAX_RED_PANDA_PROJECTS = 10;

const InformationProjectInlineEdit = ({
  options,
  selectedRedPandaProjectOptions,
  legacyProject,
  isPredUser,
  allowEdit = true,
  onSave,
}) => {
  const externalSelected = useMemo(
    () => (Array.isArray(selectedRedPandaProjectOptions) ? selectedRedPandaProjectOptions : []),
    [selectedRedPandaProjectOptions],
  );
  const externalLegacy = typeof legacyProject === "string" ? legacyProject : "";

  const computeAutoManual = (selected, legacy) =>
    !isPredUser || ((selected || []).length === 0 && !!(legacy && legacy.trim()));

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [draftManual, setDraftManual] = useState(externalLegacy);
  const [draftProjects, setDraftProjects] = useState(externalSelected);
  const [isManualMode, setIsManualMode] = useState(
    computeAutoManual(externalSelected, externalLegacy),
  );

  const effective = committed || {
    redPandaProjectOptions: externalSelected,
    project: externalLegacy,
  };

  useEffect(() => {
    if (!isEditing) {
      const selected = effective.redPandaProjectOptions || [];
      setDraftProjects(selected);
      setDraftManual(effective.project || "");
      setIsManualMode(computeAutoManual(selected, effective.project || ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, committed, selectedRedPandaProjectOptions, legacyProject, isPredUser]);

  const displayChips = useMemo(() => {
    const list = effective.redPandaProjectOptions || [];
    return list.map((opt) => ({
      key: opt?.value?.projectId || opt?.name || "",
      label: opt?.name || "-",
    }));
  }, [effective.redPandaProjectOptions]);

  const handleStartEdit = () => setIsEditing(true);

  const handleCancel = () => {
    const selected = effective.redPandaProjectOptions || [];
    setDraftProjects(selected);
    setDraftManual(effective.project || "");
    setIsManualMode(computeAutoManual(selected, effective.project || ""));
    setIsEditing(false);
  };

  const handleToggleManual = () => {
    const next = !isManualMode;
    setIsManualMode(next);
    if (next) {
      setDraftProjects([]);
    } else {
      setDraftManual("");
    }
  };

  const handleSave = async () => {
    if (saving || !hasChanges) return;
    const payload = isManualMode
      ? { redPandaProjects: [], project: draftManual.trim() || null }
      : { redPandaProjects: draftProjects || [], project: null };
    setSaving(true);
    try {
      const ok = await onSave(payload);
      if (ok !== false) {
        setCommitted({
          redPandaProjectOptions: isManualMode ? [] : (draftProjects || []),
          project: isManualMode ? (draftManual.trim() || "") : "",
        });
        setIsEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const projectIdOf = (opt) => opt?.value?.projectId || opt?.name || "";

  const hasChanges = useMemo(() => {
    const currentSelected = effective.redPandaProjectOptions || [];
    const currentLegacy = (effective.project || "").trim();
    const currentlyManual = computeAutoManual(currentSelected, currentLegacy);
    if (currentlyManual !== isManualMode) return true;
    if (isManualMode) {
      return draftManual.trim() !== currentLegacy;
    }
    const draftIds = (draftProjects || []).map(projectIdOf).filter(Boolean).sort();
    const currentIds = currentSelected.map(projectIdOf).filter(Boolean).sort();
    if (draftIds.length !== currentIds.length) return true;
    return draftIds.some((id, i) => id !== currentIds[i]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftProjects, draftManual, isManualMode, effective.redPandaProjectOptions, effective.project, isPredUser]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <OwcTypography variant="caption">Project</OwcTypography>
        {allowEdit && !isEditing && (
          <OwcIconButton
            icon="edit"
            type="outlined"
            flat
            onClick={handleStartEdit}
            style={{ color: "#0B41CD" }}
            aria-label="Edit Project"
          />
        )}
      </div>

      {!isEditing && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {displayChips.length > 0 ? (
            displayChips.map((d) => (
              <OwcChip key={d.key} round outlined>
                {d.label}
              </OwcChip>
            ))
          ) : effective.project && effective.project.trim() ? (
            <OwcTypography variant="subtitle2">{effective.project}</OwcTypography>
          ) : (
            <OwcTypography variant="subtitle2">No project selected</OwcTypography>
          )}
        </div>
      )}

      {isEditing && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {isPredUser && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <OwcCheckbox
                checked={isManualMode}
                onInputChange={handleToggleManual}
                style={{ margin: 0 }}
              />
              <OwcTypography style={{ fontSize: "14px" }}>
                Enter project manually
              </OwcTypography>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            {isManualMode ? (
              <OwcInput
                label="Project"
                style={{ width: "100%", flex: 1 }}
                value={draftManual}
                maxLength={MANUAL_PROJECT_MAX_LENGTH}
                onInputChange={(ev) => setDraftManual(ev.detail ?? "")}
              />
            ) : (
              <Autocomplete
                multiple
                disableCloseOnSelect
                value={draftProjects}
                onChange={(_e, value) => {
                  const limited = Array.isArray(value)
                    ? value.slice(0, MAX_RED_PANDA_PROJECTS)
                    : [];
                  setDraftProjects(limited);
                }}
                options={options || []}
                getOptionLabel={(opt) => opt?.name || ""}
                isOptionEqualToValue={(opt, val) =>
                  opt?.value?.projectId === val?.value?.projectId
                }
                fullWidth
                size="small"
                sx={{ minWidth: 240, flex: 1 }}
                componentsProps={{ popper: { sx: { zIndex: 99999 } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Project"
                    placeholder="Select project(s)"
                  />
                )}
              />
            )}
            <div style={{ display: "flex", gap: "4px", paddingTop: "8px" }}>
              {saving ? (
                <OwcProgressSpinner diameter={24} />
              ) : (
                <>
                  <OwcIconButton
                    icon="circle_confirm"
                    type="filled"
                    flat
                    onClick={handleSave}
                    disabled={!hasChanges}
                    style={{ color: hasChanges ? "#28A745" : undefined }}
                    aria-label="Save"
                  />
                  <OwcIconButton
                    icon="circle_clear"
                    type="filled"
                    flat
                    onClick={handleCancel}
                    style={{ color: "#DC3545" }}
                    aria-label="Cancel"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

InformationProjectInlineEdit.propTypes = {
  options: PropTypes.array.isRequired,
  selectedRedPandaProjectOptions: PropTypes.array,
  legacyProject: PropTypes.string,
  isPredUser: PropTypes.bool,
  allowEdit: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

export default InformationProjectInlineEdit;